import { ref, readonly } from "vue";
import { fetchSignatureTemplates } from "@/features/signature/services/api";
import type { SignatureTemplate, UserTemplateData } from "@/shared/types";
import { templateCache } from "@/features/signature/services/template-cache";
import { userDataCache } from "@/shared/services/user-data-cache";
import { useToast } from "@/shared/composables/useToast";

const templates = ref<SignatureTemplate[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

/**
 * Composable de gerenciamento de templates de assinatura e dados do usuário.
 *
 * @returns Objeto reativo com lista de templates, estado de carregamento e métodos auxiliares.
 *
 * @example
 * ```typescript
 * const { templates, loadTemplates, loadUserInfo } = useTemplates();
 * await loadTemplates("usuario@empresa.com");
 * const userData = loadUserInfo();
 * ```
 */
export function useTemplates() {
  const toast = useToast();
  
  /**
   * Busca os templates de assinatura disponíveis para o e-mail informado.
   *
   * @param userEmail - E-mail do usuário para filtrar os templates disponíveis.
   * @param userName - Nome do usuário, usado pelo backend para auto-provisionar o
   * recipient na primeira chamada.
   * @returns Promise que resolve quando os templates são carregados.
   *
   * @example
   * ```typescript
   * const { loadTemplates } = useTemplates();
   * await loadTemplates("usuario@empresa.com", "Fulano de Tal");
   * ```
   */
  async function loadTemplates(userEmail: string, userName?: string): Promise<void> {
    // Exibe cache imediatamente se disponível, evita tela de loading completa
    const cached = templateCache.get();
    if (cached && cached.length > 0) {
      templates.value = cached;
      isLoading.value = false;
    } else {
      isLoading.value = true;
    }
    error.value = null;

    try {
      const result = await fetchSignatureTemplates(userEmail, userName);
      if (result && result.length > 0) {
        templates.value = result;
      } else if (!cached || cached.length === 0) {
        templates.value = [];
        error.value = "Nenhum template disponível.";
      }
    } catch (err) {
      console.error("[useTemplates] Erro:", err);
      if (!cached || cached.length === 0) {
        error.value = "Erro ao carregar templates.";
        templates.value = [];
      }
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Recupera os dados do usuário salvos no localStorage ou roaming settings do Outlook.
   *
   * @returns Dados normalizados do usuário ou `null` se não encontrados ou inválidos.
   *
   * @example
   * ```typescript
   * const { loadUserInfo } = useTemplates();
   * const userData = loadUserInfo();
   * if (userData) {
   *   console.log(userData.displayName);
   * }
   * ```
   */
  function loadUserInfo(): UserTemplateData | null {
    return userDataCache.get();
  }

  /**
   * Limpa o cache de templates e redefine o estado para os valores iniciais.
   *
   * @example
   * ```typescript
   * const { clearCache } = useTemplates();
   * clearCache();
   * ```
   */
  function clearCache(): void {
    templateCache.clear();
    templates.value = [];
    error.value = null;
  }

  return {
    templates: readonly(templates),
    isLoading: readonly(isLoading),
    error: readonly(error),
    loadTemplates,
    loadUserInfo,
    clearCache,
  };
}
